import { Zap, Info } from "lucide-react";
import { Tooltip } from 'react-tooltip';
import { formatNumber } from '@/utils/formatNumber';

interface SolarCardProps {
  ratio: number;
  solarSurplus: number;
  irradiance: number;
}

export function SolarCard({ ratio, solarSurplus, irradiance }: SolarCardProps) {
  return (
    <div className="bg-white rounded-[10px] shadow-card p-2 border border-light-grey gap-[8px] flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
            <Zap className="h-3 w-3 text-primary" />
          </div>
          <h3 className="text-dark-blue heading-7">Solar Surplus Rate</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="heading-6">{formatNumber(ratio, 1)}</span>
          <span className="paragraph-12">%</span>
        </div>
      </div>

      <div className="flex gap-2 flex-1 min-h-0">
        {/* Solar Surplus */}
        <div className="flex flex-col justify-between flex-1 pt-[6px] pb-[6px] pl-[8px] pr-[8px] bg-bg border border-light-grey rounded-[8px]">
          <div className="flex items-center gap-1">
            <div className="paragraph-11 text-dark">Solar Surplus</div>
            <div
              data-tooltip-id="solar-surplus-tooltip"
              data-tooltip-place="right"
              className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
            >
              <Info className="inline-block h-[10px] w-[10px] text-primary" />
            </div>
            <Tooltip arrowColor='bg-white' id="solar-surplus-tooltip" opacity={1} style={{
              backgroundColor: 'white',
              padding: '4px 6px',
              gap: '8px',
              width: "240px",
              boxShadow: "-4px -4px 20px 0px #7090B03D",
              backdropFilter: "blur(20px)",
            }}>
              <p className='paragraph-9 text-dark-blue'>Solar Surplus = Expected Active Power - Total Power Consumption</p>
              <p className='paragraph-9 text-dark-blue mt-1'>Expected Active Power = IRR × DC Input (999.6 kW) × PR Ratio (0.8)</p>
            </Tooltip>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="heading-6">{formatNumber(solarSurplus, 1)}</span>
            <span className="paragraph-12">kW</span>
          </div>
        </div>

        {/* Irradiance */}
        <div className="flex flex-col justify-between flex-1 pt-[6px] pb-[6px] pl-[8px] pr-[8px] bg-bg border border-light-grey rounded-[8px]">
          <div className="paragraph-11 text-dark">Irradiance</div>
          <div className="flex items-baseline gap-1">
            <span className="heading-6">{formatNumber(irradiance, 1)}</span>
            <span className="paragraph-12">W/m<sup>2</sup></span>
          </div>
        </div>
      </div>
    </div>
  );
}